package vegasful.admin.views

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Application
import vegasful.admin.admin
import vegasful.admin.components.ButtonStyles
import vegasful.admin.components.box
import vegasful.admin.components.entityView
import vegasful.admin.components.spinner

/**
 * Batch social-image generation page.
 *
 * Receives a list of event ids (typically from the Today page) and fans out
 * one `generateSocialImage` mutation per event in parallel, then polls each.
 * Results are rendered as a grid of cards so the admin can review and
 * download in one place.
 *
 * No persistence — closing the page abandons in-flight polls. The server
 * still finishes the generations, but the URLs are lost. If that becomes a
 * problem, promote to a server-side batch mutation.
 */
@Composable
fun socialImageBatch(ids: List<String>, options: SlideOptions = SlideOptions()) {
    val states = remember { mutableStateMapOf<String, ViewState>() }
    val names = remember { mutableStateMapOf<String, String>() }

    LaunchedEffect(ids.joinToString(",")) {
        ids.forEach { id ->
            states[id] = ViewState.Submitting
        }

        ids.forEach { id ->
            launch {
                try {
                    val event = Application.mutate {
                        admin {
                            events {
                                event(id) {
                                    layers {
                                        this.name
                                    }
                                }
                            }
                        }
                    }.admin.events.event
                    event.layers.firstNotNullOfOrNull { it.name }?.let {
                        names[id] = it
                    }
                } catch (_: Throwable) {
                    // header lookup is best-effort
                }
            }

            launch {
                try {
                    val futureUrl = startGeneration(id, options)
                    states[id] = ViewState.Polling(futureUrl)
                    states[id] = pollForImage(id, futureUrl)
                } catch (t: Throwable) {
                    states[id] = ViewState.Failed(t.message ?: "Unknown error")
                }
            }
        }
    }

    val total = ids.size
    val completed = states.values.count { it is ViewState.Ready || it is ViewState.Failed }
    val readyCount = states.values.count { it is ViewState.Ready }

    contentContainer {
        entityView {
            title = "Batch Social Images"
            subTitle = "$completed of $total complete · $readyCount ready"

            breadcrumbs {
                crumb("Today", "content/today")
            }

            content {
                box({
                    title = "Generated Images"
                }) {
                    if (ids.isEmpty()) {
                        Div { Text("No events selected.") }
                    } else {
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(auto-fill, minmax(280px, 1fr))")
                                property("gap", "16px")
                            }
                        }) {
                            ids.forEach { id ->
                                batchCard(
                                    eventId = id,
                                    name = names[id],
                                    state = states[id] ?: ViewState.Submitting,
                                    onRetry = {
                                        val params = listOf(
                                            "ids=$id",
                                            "renderer=${options.renderer.name}",
                                            "dateStyle=${options.dateStyle.name}",
                                            "omitTime=${options.omitTime}",
                                            "includeTagline=${options.includeTagline}",
                                            "includeVenue=${options.includeVenue}",
                                            "forceCover=${options.forceCover}",
                                        ).joinToString("&")
                                        Application.navigation.navigate("content/today/batch?$params")
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun batchCard(
    eventId: String,
    name: String?,
    state: ViewState,
    onRetry: () -> Unit
) {
    Div({
        style {
            border(1.px, LineStyle.Solid, Color("#ddd"))
            property("border-radius", "4px")
            padding(12.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("gap", "8px")
            backgroundColor(Color.white)
        }
    }) {
        Div({
            style {
                fontWeight("bold")
                fontSize((0.95).cssRem)
            }
        }) {
            A(href = "#content/events/$eventId") {
                Text(name ?: eventId)
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                minHeight(160.px)
                backgroundColor(Color("#fafafa"))
                property("border-radius", "3px")
            }
        }) {
            when (state) {
                ViewState.Idle, ViewState.Submitting -> {
                    spinner()
                }

                is ViewState.Polling -> {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Center)
                            property("gap", "8px")
                        }
                    }) {
                        spinner()
                        Span({ style { fontSize((0.8).cssRem); opacity(.7) } }) {
                            Text("Generating…")
                        }
                    }
                }

                is ViewState.Ready -> {
                    Img(src = state.url) {
                        attr("alt", "Social image")
                        style {
                            property("max-width", "100%")
                            property("height", "auto")
                            property("border-radius", "3px")
                        }
                    }
                }

                is ViewState.Failed -> {
                    Div({
                        style {
                            color(Color("#b00020"))
                            fontSize((0.85).cssRem)
                            padding(8.px)
                            textAlign("center")
                        }
                    }) {
                        Text(state.message)
                    }
                }
            }
        }

        if (state is ViewState.Ready) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("gap", "8px")
                }
            }) {
                A(href = state.url, attrs = {
                    attr("download", downloadFilenameFor(state.url))
                    classes(ButtonStyles.button, ButtonStyles.primaryButton)
                    style { property("text-decoration", "none") }
                }) {
                    Text("Download")
                }
                A(href = state.url, attrs = { attr("target", "_blank") }) {
                    Span({ style { fontSize((0.8).cssRem); opacity(.7) } }) {
                        Text("Open")
                    }
                }
            }
        } else if (state is ViewState.Failed) {
            Div {
                A(href = "#", attrs = {
                    onClick {
                        it.preventDefault()
                        onRetry()
                    }
                }) { Text("Retry") }
            }
        }
    }
}
